/*
  사원증 결제 관리 — 관리 모달 (PROD-23505)

  사용자 정보(read-only) + 사원증 결제정보 입력 한 줄 + 수정 기록.
  상세는 별도 API 로 다시 읽는다(목록 행은 응답이 오기 전 초기값으로만 쓴다).
  권한은 서버가 판정한다(부서 권한이면 대상 사용자가 권한 부서 하위여야 함).
  저장은 입력값이 비어 있으면 DELETE, 값이 있으면 PUT 이다.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query';
import { Button, Form, Icon, Modal } from 'semantic-ui-react';
import styled from 'styled-components';

import {
  getEmployeeCardPaymentDetail,
  getEmployeeCardPaymentHistory,
  resolveSaveAction,
  saveEmployeeCardPayment
} from 'apis/employeeCardPayment';
import { TableForm } from 'components/Commons';
import { EMPLOYEE_CARD_PAYMENT_CODE_MAX_LENGTH, normalizeEmployeeCardPaymentCode } from 'constants/employeeCardPayment';
import cm from 'helpers/commons';
import toastr from 'helpers/toastr';

/*
  사원증 결제정보는 최대 30자(영문 대문자·숫자·하이픈)라 가장 긴 값 기준으로 폭을 고정한다.
  값 길이에 따라 열 너비가 행마다 달라 보이지 않게 하려는 것이다.
*/
const HISTORY_CODE_WIDTH = 270;

const HISTORY_ACTION_WIDTH = 52;
const HISTORY_DATE_WIDTH = 136; // '2026-09-01 10:12' 가 한 줄에 들어갈 폭

/*
  네 열의 합이 620px 을 넘으면 마지막 '수정일시' 가 줄바꿈된다.
  (모달 680 − Modal.Content 좌우 여백 42 − 표 테두리 2 − 기록이 많을 때 생기는 세로 스크롤바 16)
  수정자가 남는 폭을 가져가되 22px 는 여유로 남긴다. 넘치는 이름은 말줄임한다(한글 1글자 ≈ 14px).
*/
const HISTORY_EDITOR_WIDTH = 140;
const HISTORY_EDITOR_TEXT_WIDTH = HISTORY_EDITOR_WIDTH - 24;

const HISTORY_COLUMNS = [
  { header: '사원증 결제정보', name: 'barCode', textAlign: 'left', width: HISTORY_CODE_WIDTH },
  { header: '구분', name: 'action', textAlign: 'left', width: HISTORY_ACTION_WIDTH },
  { header: '수정자', name: 'userName', textAlign: 'left', width: HISTORY_EDITOR_WIDTH },
  { header: '수정일시', name: 'regDate', textAlign: 'left', width: HISTORY_DATE_WIDTH }
];

// 한 번에 불러오는 기록 수. 바닥까지 스크롤하면 이만큼씩 더 불러온다.
const HISTORY_PAGE_SIZE = 10;

// 바닥에서 이만큼 남으면 다음 묶음을 부른다
const HISTORY_SCROLL_THRESHOLD = 24;

/*
  기록이 적든 많든 모달 높이가 같아야 한다.
  표 높이를 5줄 기준으로 고정해, 기록이 적으면 표 안쪽이 비고 많으면 세로로 스크롤한다.

  행 높이를 semantic 의 em/rem 패딩에 맡기면 글꼴·테마가 바뀔 때 계산이 어긋나므로 직접 정한다.
*/
const HISTORY_HEADER_HEIGHT = 44;
const HISTORY_ROW_HEIGHT = 40;
const HISTORY_VISIBLE_ROWS = 5;
const HISTORY_TABLE_BORDERS = 2; // 감싼 박스의 위아래 테두리 (셀 테두리는 아래 box-sizing 으로 높이에 포함된다)
const HISTORY_TABLE_HEIGHT = HISTORY_HEADER_HEIGHT + HISTORY_ROW_HEIGHT * HISTORY_VISIBLE_ROWS + HISTORY_TABLE_BORDERS;

const FormWrapper = styled.div`
  .ui.form .inline.field {
    & > label {
      width: 120px;
      color: #000;
      /* semantic 기본값이 0.92857143em(13px) 이라 본문과 어긋난다 */
      font-size: 14px;
    }
    .ui.input input {
      padding: 10px;
      /*
        입력값(최대 30자, 약 30ch)보다 placeholder 가 길어서 placeholder 기준으로 폭을 잡는다.
        잘리면 이 값만 키우면 된다.
      */
      width: 400px;
    }
  }

  .code-help {
    /* label 폭(120px)만큼 들여써 입력칸 아래에 맞춘다 */
    padding-left: 120px;
    color: #8e8e8e;
    font-size: 14px;
    line-height: 1.5;
  }

  /* 거절 사유는 고쳐야 할 입력칸 바로 아래에 붙인다 */
  .code-error {
    padding-left: 120px;
    margin-bottom: 2px;
    color: #ff0000;
    font-size: 14px;
    line-height: 1.5;
  }

  .code-error .icon {
    margin-right: 4px;
  }
`;

const HistorySection = styled.div`
  margin-top: 24px;

  .history-title {
    margin: 0px 0px 8px;
    color: #000000;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.5;
  }

  /* 5줄 높이로 고정한다. 기록이 적으면 표 안쪽이 남고, 많으면 세로로 스크롤한다 */
  .history-table {
    height: ${HISTORY_TABLE_HEIGHT}px;
    border: 1px solid #dcdcdc;
    /* 폭 합이 모달을 넘으면 모달이 아니라 이 표만 스크롤한다. 세로는 더 불러오기용 */
    overflow: auto;

    .tb-common.ui.table {
      border: none;
      margin: 0px;
    }

    /*
      높이를 셀에서 직접 정한다(세로 패딩은 그 계산에 섞이지 않도록 뺀다).
      테두리까지 높이에 포함되도록 border-box 로 둬야 위 HISTORY_TABLE_HEIGHT 계산과 어긋나지 않는다.
      머리글은 스크롤해도 남는다(IE11 은 sticky 미지원이라 함께 올라간다).
    */
    .ui.table thead th {
      position: sticky;
      top: 0px;
      z-index: 1;
      box-sizing: border-box;
      height: ${HISTORY_HEADER_HEIGHT}px;
      padding-top: 0px;
      padding-bottom: 0px;
      background: #ffffff;
    }
    /*
      표가 내용보다 길어 아래가 비면 감싼 박스의 테두리가 멀어져 마지막 줄의 밑선이 사라진다.
      줄마다 밑선을 그어 기록 수와 무관하게 같게 보이도록 한다.
    */
    .ui.table tbody td {
      box-sizing: border-box;
      height: ${HISTORY_ROW_HEIGHT}px;
      padding-top: 0px;
      padding-bottom: 0px;
      border-bottom: 1px solid #dcdcdc;
    }
  }

  /* 글자 수로 자르지 않고 폭 기준(text-overflow)으로 자른다 */
  .editor-cell {
    display: block;
    max-width: ${HISTORY_EDITOR_TEXT_WIDTH}px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

`;

const EmployeeCardPaymentModal = ({ open, row, onClose }) => {
  const queryClient = useQueryClient();
  const [barCode, setBarCode] = useState('');
  // 서버가 입력값을 거절한 사유(중복 등). 토스트 대신 입력칸 아래에 보여준다.
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const userId = row ? row.userId : null;
  const { data: detail } = useQuery(['employeeCardPaymentDetail', userId], () => getEmployeeCardPaymentDetail(userId), {
    enabled: !!(open && userId)
  });

  // 상세 응답이 오기 전에는 목록 행 값을 그대로 쓴다.
  const info = detail || row;
  const barCodeInfo = info ? info.barCodeInfo : null;
  const barCodeStaticIdx = info ? info.barCodeStaticIdx : null;

  useEffect(() => {
    // 삭제된(isActive:false) 건은 마지막 값이 내려오지만 빈칸으로 보여준다.
    // normalizeRow 가 등록 상태일 때만 barCode 를 채워주므로 그대로 쓴다.
    setBarCode(open && info ? info.barCode || '' : '');
    setErrorMessage('');
  }, [open, info]);

  /*
    바닥까지 스크롤하면 다음 묶음을 이어 붙인다.
    페이지를 갈아끼우는 방식과 달리 이미 받은 줄이 사라지지 않아 스크롤이 튀지 않는다.
  */
  const {
    data: historyPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(
    ['employeeCardPaymentHistory', barCodeStaticIdx],
    ({ pageParam = 1 }) => getEmployeeCardPaymentHistory(barCodeStaticIdx, { page: pageParam, size: HISTORY_PAGE_SIZE }),
    {
      enabled: !!(open && barCodeStaticIdx),
      getNextPageParam: (lastPage, pages) => {
        const loaded = pages.reduce((total, page) => total + page.content.length, 0);
        return loaded < lastPage.totalCount ? pages.length + 1 : undefined;
      }
    }
  );

  const historyRows = useMemo(
    () => (historyPages ? historyPages.pages.reduce((all, page) => all.concat(page.content), []) : []),
    [historyPages]
  );

  const handleHistoryScroll = (e) => {
    if (!hasNextPage || isFetchingNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - scrollTop - clientHeight <= HISTORY_SCROLL_THRESHOLD) fetchNextPage();
  };

  const historyTableData = useMemo(() => {
    const rows = historyRows;
    const thead = HISTORY_COLUMNS.map((column) => ({ data: column.header, ...column }));

    const cellValue = (column, item) => {
      if (column.name === 'action') return item.action ? item.action.text : null;
      if (column.name === 'regDate') return cm.yyyyMMddhhmm(item.regDate);
      // 잘린 이름은 마우스를 올려 전체를 확인할 수 있게 한다
      if (column.name === 'userName' && item.userName) {
        return (
          <span className="editor-cell" title={item.userName}>
            {item.userName}
          </span>
        );
      }
      return item[column.name];
    };

    const tbody = rows.map((item) => ({
      row: HISTORY_COLUMNS.map((column) => ({ ...column, data: cellValue(column, item) || '-' }))
    }));

    return {
      header: { main: thead },
      body: { main: tbody.length ? tbody : undefined, message: '수정 이력이 존재하지 않습니다' }
    };
  }, [historyRows]);

  const historyTotalCount = historyPages ? historyPages.pages[0].totalCount : 0;

  if (!open || !info) return null;

  /*
    타이핑·붙여넣기 모두 입력 즉시 정규화해 저장될 값 그대로 보여준다.
    허용 문자셋(A-Z 0-9 -) 판정은 서버가 한다 — 한글·특수문자는 그대로 두고, 저장 시 거부 사유를 입력칸 아래에 띄운다.
    maxLength 속성은 정규화 전 길이를 세서 공백 섞인 값을 붙여넣으면 잘리므로 쓰지 않고 여기서 자른다.
  */
  const handleBarCodeChange = (e) => {
    const input = e.target;
    const { value, selectionStart } = input;
    const next = normalizeEmployeeCardPaymentCode(value);
    // 이미 꽉 찬 상태에서 더 넣으면 받지 않는다(뒤 글자가 밀려 잘리지 않도록).
    const isFull =
      next.length > EMPLOYEE_CARD_PAYMENT_CODE_MAX_LENGTH && barCode.length >= EMPLOYEE_CARD_PAYMENT_CODE_MAX_LENGTH;
    const nextValue = isFull ? barCode : next.slice(0, EMPLOYEE_CARD_PAYMENT_CODE_MAX_LENGTH);
    // 꽉 찼으면 넣으려던 글자 수만큼 앞(원래 자리), 아니면 커서 앞부분을 정규화한 길이
    const caret = isFull
      ? Math.max(selectionStart - (value.length - barCode.length), 0)
      : Math.min(
          normalizeEmployeeCardPaymentCode(value.slice(0, selectionStart)).length,
          EMPLOYEE_CARD_PAYMENT_CODE_MAX_LENGTH
        );

    /*
      바꾼 값을 React 가 다시 쓰게 두면 커서가 끝으로 튄 뒤에야 되돌릴 수 있는데,
      한글 조합 중에는 그 사이에 다음 글자가 끝에서 조합돼 커서가 끝에 머문다.
      값과 커서를 여기서 바로 맞춰 둔다(화면 값이 state 와 같으면 React 는 다시 쓰지 않는다).
    */
    if (value !== nextValue) {
      input.value = nextValue;
      input.setSelectionRange(caret, caret);
    }

    if (nextValue !== barCode) {
      // 고치기 시작하면 지난 사유는 지운다
      setErrorMessage('');
      setBarCode(nextValue);
    }
  };

  const handleSubmit = async () => {
    const action = resolveSaveAction(barCodeInfo, barCode);

    // 값이 그대로거나 미등록 상태에서 빈칸이면 보낼 것이 없다.
    // 이어서 고칠 수 있도록 모달은 닫지 않고 알리기만 한다.
    if (action === 'NONE') {
      toastr('top-right', 'info', '변경된 내용이 없습니다.');
      return;
    }
    if (isSaving) return;

    setIsSaving(true);
    setErrorMessage('');
    try {
      await saveEmployeeCardPayment(userId, barCode);

      toastr(
        'top-right',
        'success',
        action === 'DELETE' ? '사원증 결제정보가 삭제되었습니다.' : '사원증 결제정보가 저장되었습니다.'
      );

      queryClient.invalidateQueries('employeeCardPaymentList');
      queryClient.invalidateQueries(['employeeCardPaymentDetail', userId]);
      queryClient.invalidateQueries(['employeeCardPaymentHistory', barCodeStaticIdx]);
      onClose();
    } catch (error) {
      const { response } = error || {};
      const message = (response && response.data && response.data.message) || '';

      /*
        서버가 사유를 내려준 경우(이미 사용 중인 값 등)는 입력값 문제라
        입력칸 아래에 붙여야 어디를 고쳐야 하는지 바로 보인다.
        사유 없이 실패한 통신 오류만 토스트로 알린다.
      */
      if (message) {
        setErrorMessage(message);
      } else {
        toastr('top-right', 'error', '사원증 결제정보 저장에 실패했습니다.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    /*
      라벨(120px) + 입력칸(400px)이 기본이고, 수정 기록 표(622px)가 잘리지 않아야 한다.
      Modal.Content 좌우 여백(21px×2)과 표 테두리(2px)를 빼면 636px 이 남는다.
    */
    <Modal open={open} onClose={onClose} style={{ width: 680 }}>
      <Modal.Header style={{ padding: '12px 20px' }}>사원증 결제정보 상세</Modal.Header>
      <Modal.Content>
        <FormWrapper>
          <Form>
            <Form.Field inline label="ID" control={() => <span>{info.signId || '-'}</span>} />
            <Form.Field inline label="이름" control={() => <span>{info.name || '-'}</span>} />
            <Form.Field inline label="식대그룹" control={() => <span>{info.groupName || '-'}</span>} />
            <Form.Field
              inline
              label="부서"
              control={() => <span style={{ width: '280px' }}>{info.orgPathName || '-'}</span>}
            />
            <Form.Field inline label="이메일" control={() => <span>{info.email || '-'}</span>} />
            <Form.Field
              inline
              label="휴대전화번호"
              control={() => <span>{info.phone ? cm.phoneFormat(info.phone) : '-'}</span>}
            />
            <Form.Field inline label="사원번호" control={() => <span>{info.employeeId || '-'}</span>} />
            <Form.Field inline label="사용자 상태" control={() => <span>{info.statusText || '-'}</span>} />
            <Form.Field
              inline
              label="가입일"
              control={() => <span>{info.joinDate ? cm.yyyyMMdd(info.joinDate) : '-'}</span>}
            />
            <Form.Input
              inline
              error={!!errorMessage}
              label="사원증 결제정보"
              placeholder={`영문 대문자·숫자·하이픈(-) / 최대 ${EMPLOYEE_CARD_PAYMENT_CODE_MAX_LENGTH}자 / 회사 내 중복 불가`}
              value={barCode}
              onChange={handleBarCodeChange}
            />
            {errorMessage ? (
              <div className="code-error">
                <Icon name="exclamation circle" />
                {errorMessage}
              </div>
            ) : null}
            <div className="code-help">*값을 지우고 저장할 경우, 해당 사원증으로는 결제가 중지됩니다.</div>
          </Form>
        </FormWrapper>

        {historyTotalCount > 0 ? (
          <HistorySection>
            <div className="history-title">수정 기록</div>
            <div className="history-table" onScroll={handleHistoryScroll}>
              <TableForm selectable="true" tabledata={historyTableData} />
            </div>
          </HistorySection>
        ) : null}
      </Modal.Content>
      {/* 저장 버튼은 '저장 중...' 으로 길어져도 줄바꿈되지 않을 폭이어야 높이가 그대로다 */}
      <Modal.Actions style={{ padding: '12px 20px' }}>
        <Button content="취소" style={{ width: '110px', padding: '10px 20px 9px' }} onClick={onClose} />
        <Button
          color="green"
          content={isSaving ? '저장 중...' : '저장'}
          style={{ width: '110px', padding: '10px 20px 9px' }}
          disabled={isSaving}
          onClick={handleSubmit}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default EmployeeCardPaymentModal;
